import React, {useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import Button from '../Button/Button';
import TabSwitch from '../TabSwitch/TabSwitch';
import {currencyFormat} from '../../utils/functions/Functions';
import getRetailTab from './components/getRetailTab';
import Tip from '../Tip/Tip';
import getLeaseTab from './components/getLeaseTab';
import TimeOutSession from '../TimeOutSession/TimeOutSession';
import {tagDownloadPrequalifiedOffer, tagPrequalifiedResultPagePPQ} from '../../Services/TaggingService/TaggingService';
import './OfferCard.scss';
import DownloadPdfService from '../../Services/DownloadPdfService/DownloadPdfService';
import {createPdf} from '../../utils/constants/EndPoint';

function OfferCard({appConfig, customerData}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const result = useSelector((state) => state.customerData.result.value);
  const firstName = useSelector((state) => state.customerData.firstName.value);
  const vehicleYear = useSelector((state) => state.customerData.selectedVehicle.year.value);
  const vehicleModelFromState = useSelector((state) => state.customerData.selectedVehicle.model.value);
  const vehicleModel = useSelector((state) => state.customerData.selectedVehicle.model.displayName);
  const vehicleTrim = useSelector((state) => state.customerData.selectedVehicle.trim.value);
  const standardOffer = result?.standardOffers?.[0];
  const vehicleImage = `https://build.ford.com/dig/Ford/${vehicleModelFromState}/${vehicleYear}/HD-TILE/${standardOffer.imageToken}/EXT/5/vehicle.png`;
  const msrp = currencyFormat(standardOffer.msrp)?.split('.')?.[0] || currencyFormat(standardOffer.msrp);
  const expirationDateRaw = new Date(result.expirationDate);
  const expirationDate = `${expirationDateRaw.getUTCMonth() + 1}/${expirationDateRaw.getUTCDate()}/${expirationDateRaw.getUTCFullYear()}`;
  const totalAmount = currencyFormat(result.totalApprovedAmount);
  const [specialRetail] = result.specialOffers.filter((offer) => offer?.aprRate);
  const [specialLease] = result.specialOffers.filter((offer) => !offer?.aprRate);
  const standardRetailTerm = standardOffer?.aprTerms?.find((offer) => offer.term === '72') || standardOffer?.aprTerms?.[0] || null;
  const standardRetail = standardRetailTerm ? {...standardRetailTerm, trim: `${vehicleYear} ${vehicleModel} ${standardOffer.trim}`} : null;

  const termsAndConditionsLink = (url) => {
    window.open(url, '_blank');
  };

  const tabs = [getRetailTab(specialRetail, standardRetail, appConfig), getLeaseTab(specialLease, appConfig)];

  const handleDownloadPDF = () => {
    const {offerId, effectiveDate} = result;
    let retailOffer = standardRetail ? {...standardRetail, specialOffer: false} : null;
    if (specialRetail) {
      retailOffer = {...specialRetail, specialOffer: true};
    }
    const leaseOffer = specialLease ? {...specialLease, specialOffer: true} : null;

    const params = {
      offerId,
      firstName,
      expirationDate: result.expirationDate,
      totalApprovedAmount: totalAmount,
      effectiveDate,
      vehicle: {
        model: vehicleModelFromState,
        modelName: vehicleModel,
        year: vehicleYear,
        trim: vehicleTrim,
        msrp,
        imageToken: standardOffer.imageToken
      },
      retailOffer,
      leaseOffer,
      countryCode: customerData.countryCode.value,
      stateOrProvince: customerData.stateOrProvince.value
    };
    tagDownloadPrequalifiedOffer();

    DownloadPdfService.downloadPdfOffer(appConfig.exrServiceApi + createPdf, `offer-${params.offerId}.pdf`, params, customerData);
  };

  useEffect(() => {
    const {offerId} = result;
    const showSpecialOffer = !!(specialRetail || specialLease || standardRetail);
    tagPrequalifiedResultPagePPQ(offerId, customerData, showSpecialOffer);
  }, []);

  return (
    <div className='ppq--web--offer-card'>
      <div className='ppq--web--offer-card--inner'>
        <header className='ppq--web--offer-card--header'>
          <div className='ppq--web--offer-card--header--group'>
            <span data-cs-mask='true' className='ppq--web--offer-card--header--welcome'>
              Congrats, {firstName}!
            </span>
            <h1 className='ppq--web--offer-card--header--label'>
              Prequalified Amount Up to <span className='ppq--web--offer-card--header--label--highlight'>{totalAmount}*</span>
            </h1>
            <span className='ppq--web--offer-card--header--meta'>Prequalification Expires: {expirationDate}</span>
          </div>
          <Button
            buttonStyle='primary'
            onClick={handleDownloadPDF}
            className='ppq--web--offer-card--button'
            label={
              <div className='ppq--web--offer-card--button--body'>
                <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'>
                  <path
                    d='M15.5938 11.6272L9.8125 17.2433C9.61719 17.433 9.34375 17.433 9.14844 17.2433L3.36719 11.6272C3.17188 11.4375 3.17188 11.1339 3.36719 10.9821L4.14844 10.2232C4.30469 10.0335 4.61719 10.0335 4.8125 10.2232L8.44531 13.904V4.45536C8.44531 4.22768 8.67969 4 8.91406 4H10.0078C10.2812 4 10.4766 4.22768 10.4766 4.45536V13.904L14.1484 10.2232C14.3438 10.0335 14.6563 10.0335 14.8125 10.2232L15.5938 10.9821C15.7891 11.1339 15.7891 11.4375 15.5938 11.6272ZM16.5313 19.0268C16.7656 19.0268 17 19.2545 17 19.4821V20.5446C17 20.8103 16.7656 21 16.5313 21H2.46875C2.19531 21 2 20.8103 2 20.5446V19.4821C2 19.2545 2.19531 19.0268 2.46875 19.0268H16.5313Z'
                    fill='white'
                  />
                </svg>
                <span>Download PDF</span>
              </div>
            }
          />
        </header>

        <div className='ppq--web--offer-card--divider'>
          <span className='ppq--web--offer-card--divider--label'>Your Selected Vehicle</span>
          <div className='ppq--web--offer-card--divider--body' />
        </div>

        <div className='ppq--web--offer-card--body'>
          <div className='ppq--web--offer-card--body--vehicle'>
            <h2 className='ppq--web--offer-card--body--header'>
              {vehicleYear} {vehicleModel}
            </h2>
            {vehicleTrim ? <span className='ppq--web--offer-card--body--vehicle--trim'>{vehicleTrim}</span> : null}
            <img
              className='ppq--web--offer-card--body--vehicle--image'
              src={vehicleImage}
              alt={`${vehicleYear} ${vehicleModel} ${vehicleTrim || ''}`}
            />
          </div>
          <div className='ppq--web--offer-card--body--offer'>
            <div>
              <p className='ppq--web--offer-card--body--offer--description'>Starting at</p>
              <div className='ppq--web--offer-card--body--offer--group'>
                <h3 className='ppq--web--offer-card--body--header'>{msrp}</h3>
                <Tip
                  content='[S1] Manufacturer’s Suggested Retail Price (also referred to as “MSRP”, “Base MSRP”, “Base Price” or the “Starting At” price), excludes destination/delivery charge, taxes, title, license, and registration and/or electronic filing fees, dealer fees, and total of options. For authenticated AXZ Plan customers, the price displayed may represent Plan pricing. Not all AXZ Plan customers will qualify for the Plan pricing shown and not all offers or incentives are available to AXZ Plan customers.'
                  header='Disclosure'
                >
                  <span className='ppq--web--offer-card--body--offer--hint ppq--web--hint--main'>S1</span>
                </Tip>
              </div>
            </div>

            <TabSwitch tabs={tabs} />
          </div>
        </div>
      </div>

      <span className='ppq--web--offer-card--footer'>
        * Your prequalification amount is an estimate and was determined by the information you provided online. Actual price may be
        different due to local rebates, specials, and fees. Consult your dealer for actual price, payments, and complete details, and to
        discuss your financing options. See our{' '}
        <button
          type='button'
          className='prequal--web--terms-and-conditions-link'
          onClick={() => {
            termsAndConditionsLink('./terms-and-conditions');
          }}
        >
          Terms and Conditions
        </button>
      </span>
      <TimeOutSession appConfig={appConfig} dispatch={dispatch} customerData={customerData} navigate={navigate} />
    </div>
  );
}

export default OfferCard;
